import { getDefaultBrushAttribute } from '@meitu/saas-editor-core/es/layers/brush-layer';
import {
    getDefaultMarkupAnchor,
    getDefaultMarkupAttribute,
    getDefaultMarkupLabel,
    getDefaultMarkupPoint,
} from '@meitu/saas-editor-core/es/layers/markup-layer';
import {
    BrushDasharrayType,
    BrushLinecapType,
    BrushPointType,
    BrushType,
    FuncListType,
    MarkupDasharrayType,
    MarkupLabelType,
    MarkupLinecapType,
    MarkupPointType,
    MarkupType,
    type BrushData,
    type IMarkupDrawConfig,
} from '@meitu/saas-editor-core/es/version-types';
import { getMarkupPointListMaterialConfig } from '@/editor/point-config';
import { isIntl } from '@/react-process-env';
import thumbnailOfArrowLine from '../../assets/brush-arrow-line-thumbnail.png';
import thumbnailOfBlackSquare from '../../assets/brush-black-square-thumbnail.png';
import thumbnailOfLine from '../../assets/brush-line-thumbnail.png';
import thumbnailOfWhiteCurve from '../../assets/brush-withe-curve.png';
import thumbnailOfDistanceMarkupIntl from '../../assets/markup-distance-thumbnail-intl.png';
import thumbnailOfDistanceMarkup from '../../assets/markup-distance-thumbnail.png';
import thumbnailOfDistance2MarkupIntl from '../../assets/markup-distance2-thumbnail-intl.png';
import thumbnailOfDistance2Markup from '../../assets/markup-distance2-thumbnail.png';
import thumbnailOfGuideMarkup from '../../assets/markup-guide-thumbnail.png';

export type BrushPreset = {
    id: string;
    to: 'brush' | 'vector';
    thumbnail: { src: string; size?: { width: number; height: number } };
    data: Partial<BrushData>;
    needDraw?: boolean;
};

export const getBrushPresets = () => {
    const presets: BrushPreset[] = [
        {
            id: 'line',
            thumbnail: { src: thumbnailOfLine },
            to: 'brush',
            data: {
                brushType: BrushType.Line,
                fills: [
                    {
                        color: '#000000ff',
                        dasharray: BrushDasharrayType.Solid,
                        linecap: BrushLinecapType.Round,
                        width: 9,
                        offsetX: 0,
                        offsetY: 0,
                    },
                ],
            },
        },
        {
            id: 'arrowLine',
            thumbnail: { src: thumbnailOfArrowLine },
            to: 'brush',
            data: {
                brushType: BrushType.Arrow,
                fills: [
                    {
                        color: '#000000ff',
                        dasharray: BrushDasharrayType.Solid,
                        linecap: BrushLinecapType.Square,
                        width: 12,
                        offsetX: 0,
                        offsetY: 0,
                    },
                ],
                startPoint: {
                    url: '',
                    type: BrushPointType.None,
                    offset: 2,
                    width: 0,
                    height: 0,
                },
                endPoint: {
                    type: BrushPointType.CurvedArrow,
                    url: isIntl
                        ? 'https://x-design-release.stariidata.com/fe/65aa4e81f19565725.svg'
                        : 'https://material-center.meitudata.com/material/font/65aa4e81f19565725.svg',
                    offset: 10,
                    width: 21.4,
                    height: 31,
                },
            },
        },
        {
            id: 'blackSquare',
            thumbnail: { src: thumbnailOfBlackSquare, size: { width: 46, height: 20 } },
            to: 'vector',
            data: {
                brushType: BrushType.Rect,
                fills: [
                    {
                        color: '#000000ff',
                        dasharray: BrushDasharrayType.Solid,
                        linecap: BrushLinecapType.Square,
                        width: 0,
                        offsetX: 0,
                        offsetY: 0,
                    },
                ],
                naturalWidth: 200,
                naturalHeight: 86,
            },
        },
        {
            id: 'whiteCurve',
            thumbnail: { src: thumbnailOfWhiteCurve, size: { width: 78, height: 40 } },
            to: 'brush',
            needDraw: true,
            data: {
                brushType: BrushType.Curve,
                fills: [
                    {
                        ...getDefaultBrushAttribute(),
                        color: '#ffffffff',
                        width: 20,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                ],
            },
        },
    ];

    return presets;
};

export type MarkupPreset = {
    id: string;
    thumbnail: { src: string; size?: { width: number; height: number } };
    data: Partial<IMarkupDrawConfig>;
};

export const getMarkupPresets = () => {
    const presets: MarkupPreset[] = [
        {
            id: 'distanceMarkup1',
            thumbnail: {
                src: isIntl ? thumbnailOfDistanceMarkupIntl : thumbnailOfDistanceMarkup,
                size: { width: 81, height: 40 },
            },
            data: {
                __drawClick: 3,
                __vipFuncType: FuncListType.markup,
                markupType: MarkupType.Arrow,
                fill: {
                    ...getDefaultMarkupAttribute(),
                    color: '#000000ff',
                    width: 2,
                    linecap: MarkupLinecapType.Square,
                    dasharray: MarkupDasharrayType.Solid,
                },
                label: {
                    ...getDefaultMarkupLabel(),
                    fontSize: 22,
                },
                anchor: {
                    ...getDefaultMarkupAnchor(),
                    arrowLeft: [12, -12],
                    arrowRight: [12, -12],
                },
                hasLine: true,
                startPoint: { ...getDefaultMarkupPoint() },
                endPoint: { ...getDefaultMarkupPoint() },
            },
        },
        isIntl
            ? {
                  id: 'distanceMarkup2',
                  thumbnail: {
                      src: isIntl ? thumbnailOfDistance2MarkupIntl : thumbnailOfDistance2Markup,
                      size: { width: 81, height: 40 },
                  },
                  data: {
                      __drawClick: 3,
                      __vipFuncType: FuncListType.markup,
                      markupType: MarkupType.Arrow,
                      fill: {
                          ...getDefaultMarkupAttribute(),
                          color: '#000000ff',
                          width: 2,
                          linecap: MarkupLinecapType.Square,
                          dasharray: MarkupDasharrayType.Solid,
                      },
                      label: {
                          ...getDefaultMarkupLabel(),
                          fontSize: 22,
                      },
                      anchor: {
                          ...getDefaultMarkupAnchor(),
                          arrowLeft: [12, -12],
                          arrowRight: [12, -12],
                      },
                      hasLine: true,
                      startPoint: {
                          type: MarkupPointType.SharpTriangle,
                          ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                      },
                      endPoint: {
                          type: MarkupPointType.SharpTriangle,
                          ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                      },
                  },
              }
            : {
                  id: 'guideMarkup',
                  thumbnail: { src: thumbnailOfGuideMarkup, size: { width: 81, height: 40 } },
                  data: {
                      __drawClick: 3,
                      __vipFuncType: FuncListType.markup,
                      markupType: MarkupType.Segment,
                      fill: {
                          ...getDefaultBrushAttribute(),
                          color: '#000000ff',
                          width: 3,
                          linecap: MarkupLinecapType.Round,
                          dasharray: MarkupDasharrayType.Dot,
                      },
                      label: {
                          ...getDefaultMarkupLabel(),
                          type: MarkupLabelType.custom,
                          text: 'meitu',
                          fontFamily: 'AlibabaPuHuiTi-Medium',
                          fontSize: 25,
                          color: '#ffffffff',
                          background: '#000000ff',
                      },
                      anchor: {
                          ...getDefaultMarkupAnchor(),
                      },
                      startPoint: {
                          ...getDefaultMarkupPoint(),
                      },
                      endPoint: {
                          ...getDefaultMarkupPoint(),
                      },
                  },
              },
    ];
    return presets;
};
